import { ActionIcon, Tooltip } from '@mantine/core';
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLayoutSidebarRightCollapse,
  IconLayoutSidebarRightExpand,
} from '@tabler/icons-react';

/**
 * Vertically-centered collapse toggle attached to a panel's inner edge. Render
 * it inside a `position: relative` element that carries the `collapse-pane`
 * class (the center conversation / canvas pane); the button fades in only while
 * that pane is hovered or the button is keyboard-focused (see global.css
 * `.collapse-rail`). Shared by all three inboxes and the bot-flow builder so the
 * left/right collapse affordance is identical everywhere.
 */
export function CollapseRail({
  side,
  collapsed,
  onToggle,
  label,
}: {
  side: 'left' | 'right';
  collapsed: boolean;
  onToggle: () => void;
  /** Noun for the tooltip + aria, e.g. "conversation list" → "Show conversation list". */
  label: string;
}) {
  const Icon =
    side === 'left'
      ? collapsed
        ? IconLayoutSidebarLeftExpand
        : IconLayoutSidebarLeftCollapse
      : collapsed
        ? IconLayoutSidebarRightExpand
        : IconLayoutSidebarRightCollapse;
  const tip = `${collapsed ? 'Show' : 'Hide'} ${label}`;
  return (
    <Tooltip label={tip} position={side === 'left' ? 'right' : 'left'} withinPortal>
      <ActionIcon
        className="collapse-rail"
        variant="default"
        radius="xl"
        size="sm"
        onClick={onToggle}
        aria-label={tip}
        style={{
          position: 'absolute',
          top: '50%',
          [side]: 4,
          transform: 'translateY(-50%)',
          zIndex: 6,
          boxShadow: 'var(--mantine-shadow-md)',
        }}
      >
        <Icon size={16} />
      </ActionIcon>
    </Tooltip>
  );
}
